// SuppressAlertDescriptionModal — quick-suppress modal opened from the Detection Alerts
// Actions column (v1.110.0), for Microsoft Defender `security_alert` pass-through alerts.
// Sibling of SuppressAlertTitleModal but matches on the alert `$.description` (with an
// optional `$.detectorId` gate) — the discriminating text for some approved-tool false
// positives (e.g. "ScreenConnect Client (3bc6fed378ff7169)") lives only in the description,
// not the title. Appends the entry to the FIRING rule's own
// `parameters_json.excluded_alert_descriptions` via the dedicated SuppressAlertDescription
// endpoint (which works on PROD rules — a narrow list mutation, not a full rule edit).
//
// The blast radius = the firing rule's existing scope; the modal surfaces that explicitly
// before the analyst confirms. There is no per-tenant copy here by design — scope a rule
// to an MSP (or clone it) via the rule editor for that.

import React, { useState } from 'react';
import {
  XMarkIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { suppressAlertDescription } from '../../api/client';

function splitCount(csv) {
  return (csv || '').split(',').map((s) => s.trim()).filter(Boolean).length;
}

export default function SuppressAlertDescriptionModal({ alert, ruleMeta, prefill, onClose }) {
  // Pre-filled from the alert's parsed security_alert payload ($.description / $.detectorId).
  const [description, setDescription] = useState((prefill?.description || '').trim());
  const [detectorId, setDetectorId] = useState((prefill?.detectorId || '').trim());
  // Descriptions are long/variable — 'contains' is the practical default for tool FPs.
  const [mode, setMode] = useState('contains');
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  const [applied, setApplied] = useState(false);

  const includeCount = splitCount(ruleMeta?.includedTenantIds);
  const excludeCount = splitCount(ruleMeta?.excludedTenantIds);
  const scopeText =
    includeCount > 0 ? `only ${includeCount} scoped tenant${includeCount === 1 ? '' : 's'}`
      : excludeCount > 0 ? `all tenants except ${excludeCount}`
        : 'ALL tenants';
  const scopeTone = includeCount > 0 ? 'text-sky-300' : 'text-amber-300';

  const trimmedDesc = description.trim();
  const blocker = !alert.ruleId ? 'This alert has no rule id.'
    : !trimmedDesc ? 'Enter the alert description to suppress.'
      : null;

  const handleApply = async () => {
    if (blocker) { setError(blocker); return; }
    setApplying(true);
    setError(null);
    try {
      await suppressAlertDescription(alert.ruleId, { description: trimmedDesc, mode, detectorId: detectorId.trim() });
      setApplied(true);
    } catch (err) {
      setError(err.message || 'Failed to suppress alert description');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-lg w-full max-w-xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-800">
          <div>
            <h2 className="text-lg font-semibold text-gray-100">Suppress this alert description</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {alert.ruleName ? <span className="text-gray-400">{alert.ruleName}</span> : 'Defender pass-through rule'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300 p-1">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {applied ? (
            <div className="p-4 bg-emerald-900/20 border border-emerald-700/40 rounded-lg text-sm text-emerald-200 flex items-start gap-3">
              <CheckCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Alert description suppressed.</p>
                <p className="text-emerald-300/80 mt-1">
                  Alerts whose description {mode === 'contains' ? 'contains' : 'exactly matches'}{' '}
                  <span className="font-mono">“{trimmedDesc}”</span>
                  {detectorId.trim() ? <> (detector <span className="font-mono">{detectorId.trim()}</span>)</> : null} will no longer be raised by this rule.
                  Takes effect within ~2 minutes.
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Blast radius */}
              <div className="p-3 bg-gray-800/60 border border-gray-700/60 rounded text-xs text-gray-400 flex items-start gap-2">
                <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5 text-gray-500" />
                <span>
                  This adds the description to the firing rule directly — it will be suppressed for{' '}
                  <span className={`font-medium ${scopeTone}`}>{scopeText}</span> this rule applies to (no per-tenant copy).
                  A known-malicious IP still alerts.
                </span>
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Alert description</label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
                  placeholder="e.g. ScreenConnect Client (3bc6fed378ff7169) was installed…" className="input-field w-full text-sm" />
                <p className="text-[11px] text-gray-500 mt-1">Matched case-insensitively after trimming whitespace. No wildcards / regex.</p>
              </div>

              {/* Detector ID (optional) */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Detector ID <span className="text-gray-600 normal-case">(optional — pins the drop to one alert type)</span></label>
                <input type="text" value={detectorId} onChange={(e) => setDetectorId(e.target.value)}
                  placeholder="e.g. MaliciousServiceCreationSecurityAlert" className="input-field w-full text-sm font-mono" />
              </div>

              {/* Match mode */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Match</label>
                <div className="space-y-1.5 text-sm">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="radio" name="descModeSel" checked={mode === 'contains'} onChange={() => setMode('contains')}
                      className="border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500 mt-0.5" />
                    <span className="text-gray-300 min-w-0">
                      Contains
                      <span className="text-[11px] text-gray-500 block">Suppress any description that contains this text (recommended for tool identifiers).</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="radio" name="descModeSel" checked={mode === 'exact'} onChange={() => setMode('exact')}
                      className="border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500 mt-0.5" />
                    <span className="text-gray-300 min-w-0">
                      Exact description
                      <span className="text-[11px] text-gray-500 block">Suppress only this exact description.</span>
                    </span>
                  </label>
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-900/30 border border-red-700/50 rounded text-red-300 text-sm flex items-center gap-2">
                  <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-800">
          {applied ? (
            <button onClick={onClose} className="btn-primary px-4 py-1.5 text-sm">Done</button>
          ) : (
            <>
              <button onClick={onClose} disabled={applying} className="btn-secondary px-4 py-1.5 text-sm">Cancel</button>
              <button onClick={handleApply} disabled={applying || !!blocker} className="btn-primary px-4 py-1.5 text-sm flex items-center gap-2">
                {applying && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
                Suppress description
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
